import {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  writeBatch
} from "./access/firestore";

import jsPDF from "jspdf";

import { db } from "./firebase";

import { useClinic } from "./context/ClinicContext";

import { useNotifications } from "./notifications/NotificationProvider";

import FileViewerModal
  from "./FileViewerModal";

import {
  ClipboardList,
  FileCheck2,
  Save,
  ShieldCheck,
  Sparkles,
  UserRound
} from "lucide-react";

/*
=========================================================
 HISTORIA CLÍNICA DEL PACIENTE
=========================================================

 La pantalla se construye a partir de:

 clinics/{clinicId}/historiaClinicaConfig/config

 y de los datos existentes del paciente:

 clinics/{clinicId}/pacientes/{pacienteId}

 IMPORTANTE:
 - La configuración pertenece al clinicId actual.
 - Los datos históricos del paciente NO se eliminan.
 - Los campos con "campo" en la configuración se
   sincronizan con el campo existente del paciente.
 - Las preguntas personalizadas se guardan en:
   paciente.historiaClinica.respuestas
=========================================================
*/


const SECCIONES_FIJAS = [
  {
    id: "general",
    titulo: "General"
  },
  {
    id: "direccion",
    titulo: "Dirección"
  },
  {
    id: "clinico",
    titulo: "Clínico"
  },
  {
    id: "extras",
    titulo: "Extras"
  }
];


const CONFIG_DEFAULT = {
  version: 2,

  secciones:
    SECCIONES_FIJAS.map(
      seccion => ({
        ...seccion,
        preguntas: []
      })
    )
};


/*
=========================================================
 NORMALIZAR CONFIGURACIÓN
=========================================================
*/

function normalizarConfiguracion(
  config
) {

  if (
    !config ||
    !Array.isArray(
      config.secciones
    )
  ) {

    return CONFIG_DEFAULT;

  }


  const secciones =
    SECCIONES_FIJAS.map(
      seccionFija => {

        const preguntas = [];


        config.secciones.forEach(
          seccion => {

            let corresponde =
              false;


            /*
            ============================================
             GENERAL
            ============================================
            */

            if (
              seccionFija.id ===
              "general"
            ) {

              corresponde =
                seccion.id ===
                  "general" ||
                seccion.id ===
                  "datos_generales";

            }


            /*
            ============================================
             DIRECCIÓN
            ============================================
            */

            if (
              seccionFija.id ===
              "direccion"
            ) {

              corresponde =
                seccion.id ===
                "direccion";

            }


            /*
            ============================================
             CLÍNICO
            ============================================
            */

            if (
              seccionFija.id ===
              "clinico"
            ) {

              corresponde =
                seccion.id ===
                  "clinico" ||
                seccion.id ===
                  "habitos" ||
                seccion.id ===
                  "higiene_oral" ||
                seccion.id ===
                  "dolor";

            }


            /*
            ============================================
             EXTRAS
            ============================================
            */

            if (
              seccionFija.id ===
              "extras"
            ) {

              corresponde =
                seccion.id ===
                  "extras" ||
                seccion.id ===
                  "observaciones" ||
                seccion.id ===
                  "motivo_consulta";

            }


            if (
              corresponde &&
              Array.isArray(
                seccion.preguntas
              )
            ) {

              preguntas.push(
                ...seccion.preguntas
              );

            }

          }
        );


        /*
        ================================================
         ELIMINAR DUPLICADOS
        ================================================
        */

        const ids =
          new Set();

        const preguntasUnicas =
          preguntas.filter(
            pregunta => {

              if (
                !pregunta?.id
              ) {

                return false;

              }


              if (
                ids.has(
                  pregunta.id
                )
              ) {

                return false;

              }


              ids.add(
                pregunta.id
              );

              return true;

            }
          );


        return {

          id:
            seccionFija.id,

          titulo:
            seccionFija.titulo,

          preguntas:
            preguntasUnicas

        };

      }
    );


  return {

    version: 2,

    secciones

  };

}


/*
=========================================================
 OBTENER VALOR EXISTENTE DEL PACIENTE
=========================================================
*/

function obtenerValorPaciente(
  paciente,
  data,
  pregunta
) {

  /*
  =======================================================
   1. SI YA EXISTE UNA RESPUESTA PERSONALIZADA,
      ESA TIENE PRIORIDAD
  =======================================================
  */

  if (
    pregunta?.id &&
    data?.respuestas &&
    Object.prototype.hasOwnProperty.call(
      data.respuestas,
      pregunta.id
    )
  ) {

    return data.respuestas[
      pregunta.id
    ];

  }


  const campo =
    pregunta?.campo;

  if (campo === "edad") {

    const fechaNacimiento =
      paciente?.fechaNacimiento ||
      data?.fechaNacimiento;

    return calcularEdad(
      fechaNacimiento
    );
  }

  if (!campo) {

    return "";

  }


  /*
  =======================================================
   2. CAMPO DIRECTO DEL PACIENTE

   Ejemplo:

   pregunta.campo = "diabetes"

   paciente.diabetes = true
  =======================================================
  */

  if (
    paciente &&
    Object.prototype.hasOwnProperty.call(
      paciente,
      campo
    )
  ) {

    return paciente[
      campo
    ];

  }


  /*
  =======================================================
   3. CAMPO DENTRO DE historiaClinica
  =======================================================
  */

  if (
    data &&
    Object.prototype.hasOwnProperty.call(
      data,
      campo
    )
  ) {

    return data[
      campo
    ];

  }


  /*
  =======================================================
   4. COMPATIBILIDAD CON LA ESTRUCTURA ANTERIOR
  =======================================================
  */

  const equivalencias = {

    tabaco:
      data?.habitos?.tabaquismo,

    tabaquismo:
      data?.habitos?.tabaquismo,

    alcohol:
      data?.habitos?.alcohol,

    bruxismo:
      data?.habitos?.bruxismo,

    cepilladosDia:
      data?.higiene?.cepilladosDia,

    hiloDental:
      data?.higiene?.hiloDental,

    enjuague:
      data?.higiene?.enjuague,

    peso:
      data?.peso,

    motivoConsulta:
      data?.motivoConsulta,

    observaciones:
      data?.observaciones,

    alergias:
      data?.alergias,

    medicamentos:
      data?.medicamentos

  };


  if (
    Object.prototype.hasOwnProperty.call(
      equivalencias,
      campo
    )
  ) {

    return equivalencias[
      campo
    ];

  }


  /*
  =======================================================
   5. ANTECEDENTES MÉDICOS ANTIGUOS
  =======================================================
  */

  if (
    [
      "diabetes",
      "hipertension",
      "cardiopatias",
      "epilepsia",
      "embarazo"
    ].includes(
      campo
    )
  ) {

    return data
      ?.antecedentesMedicos
      ?.[campo];

  }


  return "";

}

/*
=========================================================
 CALCULAR EDAD A PARTIR DE FECHA DE NACIMIENTO
=========================================================
*/

function calcularEdad(fechaNacimiento) {

  if (!fechaNacimiento) {
    return "";
  }

  const fecha = new Date(fechaNacimiento);

  if (Number.isNaN(fecha.getTime())) {
    return "";
  }

  const hoy = new Date();

  let edad =
    hoy.getFullYear() -
    fecha.getFullYear();

  const mes =
    hoy.getMonth() -
    fecha.getMonth();

  if (
    mes < 0 ||
    (
      mes === 0 &&
      hoy.getDate() <
        fecha.getDate()
    )
  ) {
    edad--;
  }

  return edad >= 0
    ? edad
    : "";
}

/*
=========================================================
 PREFIJO INTERNO DE WHATSAPP
=========================================================
*/

function obtenerPrefijoWhatsApp(
  countryCode
) {

  switch (countryCode) {

    case "+52":
      return "521";

    case "+1":
      return "1";

    case "+57":
      return "57";

    case "+54":
      return "54";

    case "+56":
      return "56";

    case "+51":
      return "51";

    case "+34":
      return "34";

    default:
      return (
        countryCode || ""
      ).replace("+", "");

  }

}

/*
=========================================================
 NORMALIZAR VALOR PARA EL CONTROL
=========================================================
*/

function normalizarValorInput(
  valor,
  tipo
) {

  if (
    valor === null ||
    valor === undefined
  ) {

    if (
      tipo === "checkbox" ||
      tipo === "si_no"
    ) {

      return false;

    }


    if (
      tipo ===
      "seleccion_multiple"
    ) {

      return [];

    }


    return "";

  }


  if (
    tipo ===
    "seleccion_multiple"
  ) {

    return Array.isArray(
      valor
    )
      ? valor
      : [];

  }


  return valor;

}


/*
=========================================================
 COMPONENTE PRINCIPAL
=========================================================
*/

export default function HistoriaClinicaPanel({

  paciente,

  pacienteId

}) {

  const {
    clinicId
  } = useClinic();

    const {
      notify
    } = useNotifications();

    const [
      clinicData,
      setClinicData
    ] = useState(null);

    const [
      historiasFirmadas,
      setHistoriasFirmadas
    ] = useState([]);

    const [
      historiaSeleccionada,
      setHistoriaSeleccionada
    ] = useState(null);


    const [
      historiaViewerOpen,
      setHistoriaViewerOpen
    ] = useState(false);


    const [
      historiaViewerUrl,
      setHistoriaViewerUrl
    ] = useState("");

    const [
      cargandoHistoriasFirmadas,
      setCargandoHistoriasFirmadas
    ] = useState(false);


  /*
  =======================================================
   Aceptamos paciente o pacienteId.

   Esto evita romper la integración actual.
  =======================================================
  */

  const idPaciente =
    paciente?.id ||
    pacienteId;


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    configuracionHistoria,
    setConfiguracionHistoria
  ] = useState(
    CONFIG_DEFAULT
  );


  const [
    pacienteActual,
    setPacienteActual
  ] = useState(
    paciente || null
  );


  const [
    data,
    setData
  ] = useState({

    respuestas: {},

    antecedentesMedicos: {

      diabetes: false,
      hipertension: false,
      cardiopatias: false,
      epilepsia: false,
      embarazo: false

    },

    habitos: {

      tabaquismo: false,
      alcohol: false,
      bruxismo: false

    },

    higiene: {

      cepilladosDia: 2,
      hiloDental: false,
      enjuague: false

    },

    alergias: [],

    medicamentos: [],

    motivoConsulta: "",

    peso: "",

    observaciones: ""

  });


  const [
    seccionActiva,
    setSeccionActiva
  ] = useState(
    "general"
  );


  const [
    guardando,
    setGuardando
  ] = useState(false);


  const [
    guardado,
    setGuardado
  ] = useState(true);

  const [
    colonias,
    setColonias
  ] = useState([]);

  const firmaCanvasRef =
    useRef(null);

  const firmaDibujandoRef =
    useRef(false);

  const [
    firmaTieneContenido,
    setFirmaTieneContenido
  ] = useState(false);

  const obtenerPosicionFirma =
    (event) => {

      const canvas =
        firmaCanvasRef.current;

      if (!canvas) {
        return null;
      }

      const rect =
        canvas.getBoundingClientRect();

      const escalaX =
        canvas.width /
        rect.width;

      const escalaY =
        canvas.height /
        rect.height;

      return {
        x:
          (event.clientX -
            rect.left) *
          escalaX,

        y:
          (event.clientY -
            rect.top) *
          escalaY
      };

    };


  const iniciarFirma =
    (event) => {

      const canvas =
        firmaCanvasRef.current;

      if (!canvas) {
        return;
      }

      const posicion =
        obtenerPosicionFirma(
          event
        );

      if (!posicion) {
        return;
      }

      const ctx =
        canvas.getContext("2d");

      ctx.beginPath();

      ctx.moveTo(
        posicion.x,
        posicion.y
      );

      firmaDibujandoRef.current =
        true;

      setFirmaTieneContenido(
        true
      );

      canvas.setPointerCapture(
        event.pointerId
      );

    };


  const dibujarFirma =
    (event) => {

      if (
        !firmaDibujandoRef.current
      ) {
        return;
      }

      const canvas =
        firmaCanvasRef.current;

      if (!canvas) {
        return;
      }

      const posicion =
        obtenerPosicionFirma(
          event
        );

      if (!posicion) {
        return;
      }

      const ctx =
        canvas.getContext("2d");

      ctx.lineTo(
        posicion.x,
        posicion.y
      );

      ctx.stroke();

    };


  const terminarFirma =
    (event) => {

      const canvas =
        firmaCanvasRef.current;

      firmaDibujandoRef.current =
        false;

      if (
        canvas &&
        canvas.hasPointerCapture(
          event.pointerId
        )
      ) {

        canvas.releasePointerCapture(
          event.pointerId
        );

      }

    };

    useEffect(() => {

      const canvas =
        firmaCanvasRef.current;

      if (!canvas) {
        return;
      }

      const ctx =
        canvas.getContext("2d");

      ctx.lineWidth = 2.5;

      ctx.lineCap =
        "round";

      ctx.lineJoin =
        "round";

    }, [
      seccionActiva
    ]);

  /*
  ========================================================
   CARGAR PACIENTE
  ========================================================
  */

  useEffect(() => {

    if (
      !clinicId ||
      !idPaciente
    ) {

      setLoading(false);

      return;

    }


    setLoading(true);

    const referencia = doc(
      db,
      "clinics",
      clinicId,
      "pacientes",
      idPaciente
    );

    const unsubscribe = onSnapshot(
      referencia,
      (snap) => {
        if (!snap.exists()) {
          setPacienteActual(null);
          setLoading(false);
          return;
        }

        const pacienteFirestore = { id: snap.id, ...snap.data() };
        const historia = pacienteFirestore.historiaClinica || {};

        setPacienteActual(pacienteFirestore);
        setData(prev => ({
          ...prev,
          ...historia,
          respuestas: { ...(prev.respuestas || {}), ...(historia.respuestas || {}) },
          antecedentesMedicos: { ...prev.antecedentesMedicos, ...(historia.antecedentesMedicos || {}) },
          habitos: { ...prev.habitos, ...(historia.habitos || {}) },
          higiene: { ...prev.higiene, ...(historia.higiene || {}) }
        }));
        setLoading(false);
      },
      (error) => {
        console.error("Error sincronizando historia clínica:", error);
        notify({
          type: "error",
          title: "No se pudo sincronizar la historia",
          message: "Revisa tu conexión e inténtalo de nuevo."
        });
        setLoading(false);
      }
    );

    return () => unsubscribe();

  }, [
    clinicId,
    idPaciente,
    notify
  ]);


  // =========================
  // CARGAR CONFIGURACIÓN
  // =========================

  useEffect(() => {

    if (!clinicId) return;

    const cargarConfiguracion = async () => {

      try {

        const ref = doc(
          db,
          "clinics",
          clinicId,
          "historiaClinicaConfig",
          "config"
        );

        const snap = await getDoc(ref);

        // =====================================================
        // NO EXISTE CONFIGURACIÓN
        // =====================================================

        if (!snap.exists()) {

          setConfiguracionHistoria(null);

          console.log(
            "HISTORIA CLÍNICA: no existe configuración personalizada"
          );

          return;
        }

        const configOriginal = snap.data();

        if (
          !configOriginal ||
          !Array.isArray(configOriginal.secciones)
        ) {

          setConfiguracionHistoria(null);

          console.log(
            "HISTORIA CLÍNICA: configuración inválida"
          );

          return;
        }

        // =====================================================
        // NORMALIZAR CONFIGURACIÓN
        // =====================================================

        const configNormalizada =
          normalizarConfiguracion(configOriginal);

        // =====================================================
        // MOSTRAR INMEDIATAMENTE LA CONFIGURACIÓN NORMALIZADA
        // =====================================================

        setConfiguracionHistoria(
          configNormalizada
        );

        console.log(
          "HISTORIA CLÍNICA: configuración normalizada",
          configNormalizada
        );

        // =====================================================
        // MIGRACIÓN AUTOMÁTICA
        //
        // Si la configuración guardada es diferente de la
        // configuración normalizada, significa que necesita
        // migración.
        // =====================================================

        const originalComparable = {
          ...configOriginal,
          updatedAt: undefined
        };

        const normalizadaComparable = {
          ...configNormalizada,
          updatedAt: undefined
        };

        const necesitaMigracion =
          JSON.stringify(originalComparable) !==
          JSON.stringify(normalizadaComparable);

        if (necesitaMigracion) {

          console.log(
            "HISTORIA CLÍNICA: migración automática iniciada"
          );

          await setDoc(
            ref,
            {
              ...configNormalizada
            },
            {
              merge: true
            }
          );

          console.log(
            "HISTORIA CLÍNICA: migración automática completada"
          );

        } else {

          console.log(
            "HISTORIA CLÍNICA: configuración ya actualizada"
          );

        }

      } catch (err) {

        console.error(
          "Error cargando/migrando configuración de historia clínica:",
          err
        );

        setConfiguracionHistoria(null);

      }

    };

    cargarConfiguracion();

  }, [
    clinicId
  ]);

  useEffect(() => {

    if (!clinicId) return;

    const cargarClinicData = async () => {

      try {

        const ref = doc(
          db,
          "clinics",
          clinicId
        );

        const snap = await getDoc(ref);

        if (snap.exists()) {

          setClinicData(
            snap.data()
          );

        }

      } catch (error) {

        console.error(
          "Error cargando datos del consultorio:",
          error
        );

      }

    };

    cargarClinicData();

  }, [
    clinicId
  ]);

  useEffect(() => {

    if (
      !clinicId ||
      !idPaciente
    ) {

      setHistoriasFirmadas([]);

      return;

    }


    const cargarHistoriasFirmadas =
      async () => {

        try {

          setCargandoHistoriasFirmadas(
            true
          );


          const historiasRef =
            collection(
              db,
              "clinics",
              clinicId,
              "pacientes",
              idPaciente,
              "historiasClinicasFirmadas"
            );


          const snapshot =
            await getDocs(
              historiasRef
            );


          const historias =
            snapshot.docs
              .map(
                historiaDoc => ({

                  id:
                    historiaDoc.id,

                  ...historiaDoc.data()

                })
              )
              .sort(
                (
                  a,
                  b
                ) =>
                  (b.version || 0) -
                  (a.version || 0)
              );


          setHistoriasFirmadas(
            historias
          );


        } catch (error) {

          console.error(
            "Error cargando historias clínicas firmadas:",
            error
          );


          notify({

            type: "error",

            title:
              "No se pudo cargar el registro",

            message:
              "No fue posible cargar las historias clínicas firmadas."

          });


        } finally {

          setCargandoHistoriasFirmadas(
            false
          );

        }

      };


    cargarHistoriasFirmadas();

  }, [
    clinicId,
    idPaciente,
    notify
  ]);

  /*
  ========================================================
   SECCIONES
  ========================================================
  */

  const secciones =
    useMemo(
      () => {

        if (
          !Array.isArray(
            configuracionHistoria
              ?.secciones
          )
        ) {

          return [];

        }


        return configuracionHistoria
          .secciones;

      },
      [
        configuracionHistoria
      ]
    );


  const seccion =
    secciones.find(
      item =>
        item.id ===
        seccionActiva
    ) ||
    secciones[0];

  const preguntasTotales = secciones.reduce(
    (total, item) => total + (item.preguntas || []).length,
    0
  );

  const preguntasRespondidas = secciones.reduce(
    (total, item) => total + (item.preguntas || []).filter(pregunta => {
      const valor = obtenerValorPaciente(pacienteActual, data, pregunta);
      return Array.isArray(valor)
        ? valor.length > 0
        : valor !== undefined && valor !== null && String(valor).trim() !== "";
    }).length,
    0
  );

  const porcentajeCompletado = preguntasTotales
    ? Math.min(100, Math.round((preguntasRespondidas / preguntasTotales) * 100))
    : 0;

  const preguntasPendientes = Math.max(0, preguntasTotales - preguntasRespondidas);


  /*
  ========================================================
   ACTUALIZAR RESPUESTA
  ========================================================
  */

  const actualizarRespuesta =
    (
      pregunta,
      valor
    ) => {

      setGuardado(false);

      setFirmaTieneContenido(false);

      if (firmaCanvasRef.current) {
        const canvas = firmaCanvasRef.current;
        const ctx = canvas.getContext("2d");

        ctx.clearRect(
          0,
          0,
          canvas.width,
          canvas.height
        );
      }

      setData(
        prev => {

          const siguiente = {

            ...prev,

            respuestas: {

              ...(prev.respuestas || {}),

              [pregunta.id]:
                valor

            }

          };


          /*
          ================================================
           Si la pregunta representa un campo histórico
           de VexaDent, también actualizamos ese campo.
          ================================================
          */

          if (
            pregunta.campo
          ) {

            siguiente[
              pregunta.campo
            ] = valor;


            /*
            ============================================
             Compatibilidad con antecedentes médicos
            ============================================
            */

            if (
              [
                "diabetes",
                "hipertension",
                "cardiopatias",
                "epilepsia",
                "embarazo"
              ].includes(
                pregunta.campo
              )
            ) {

              siguiente.antecedentesMedicos = {

                ...(prev.antecedentesMedicos || {}),

                [pregunta.campo]:
                  valor

              };

            }


            /*
            ============================================
             Compatibilidad con hábitos
            ============================================
            */

            if (
              pregunta.campo ===
              "tabaco" ||
              pregunta.campo ===
              "tabaquismo"
            ) {

              siguiente.habitos = {

                ...(prev.habitos || {}),

                tabaquismo:
                  valor

              };

            }


            if (
              [
                "alcohol",
                "bruxismo"
              ].includes(
                pregunta.campo
              )
            ) {

              siguiente.habitos = {

                ...(prev.habitos || {}),

                [pregunta.campo]:
                  valor

              };

            }


            /*
            ============================================
             Compatibilidad con higiene
            ============================================
            */

            if (
              [
                "cepilladosDia",
                "hiloDental",
                "enjuague"
              ].includes(
                pregunta.campo
              )
            ) {

              siguiente.higiene = {

                ...(prev.higiene || {}),

                [pregunta.campo]:
                  valor

              };

            }

          }


          return siguiente;

        }
      );

    };

  /*
  ========================================================
  BUSCAR CÓDIGO POSTAL
  ========================================================
  */

  const buscarCodigoPostal =
    async (
      pregunta,
      cp
    ) => {

      const codigoPostal =
        String(cp || "")
          .replace(/\D/g, "")
          .slice(0, 5);

      /*
      ================================================
      Guardamos primero el CP
      ================================================
      */

      actualizarRespuesta(
        pregunta,
        codigoPostal
      );

      if (
        codigoPostal.length !== 5
      ) {

        setColonias([]);

        return;

      }

      try {

        const res =
          await fetch(
            `https://api.zippopotam.us/mx/${codigoPostal}`
          );

        if (!res.ok) {

          setColonias([]);

          return;

        }

        const resultado =
          await res.json();

        const places =
          resultado.places || [];

        /*
        ================================================
        Localidades devueltas por SEPOMEX/Zippopotam
        ================================================
        */

        const listaColonias =
          places
            .map(
              lugar =>
                lugar["place name"]
            )
            .filter(Boolean);

        setColonias(
          listaColonias
        );

        /*
        ================================================
        Actualizar automáticamente dirección
        ================================================
        */

        const lugarPrincipal =
          places[0];

        if (!lugarPrincipal) {

          return;

        }

        const ciudad =
          lugarPrincipal["place name"] ||
          "";

        const estado =
          lugarPrincipal["state"] ||
          "";

        /*
        ================================================
        Actualizamos las respuestas correspondientes
        solamente si existen como preguntas configuradas.
        ================================================
        */

        const preguntasDireccion =
          configuracionHistoria
            ?.secciones
            ?.flatMap(
              seccion =>
                seccion.preguntas || []
            ) || [];

        const preguntaColonia =
          preguntasDireccion.find(
            item =>
              item.campo === "colonia"
          );

        const preguntaCiudad =
          preguntasDireccion.find(
            item =>
              item.campo === "ciudad"
          );

        const preguntaEstado =
          preguntasDireccion.find(
            item =>
              item.campo === "estado"
          );

        const preguntaPais =
          preguntasDireccion.find(
            item =>
              item.campo === "pais"
          );

        /*
        ================================================
        Colonia
        ================================================
        */

        if (
          preguntaColonia &&
          listaColonias.length > 0
        ) {

          const coloniaActual =
            obtenerValorPaciente(
              pacienteActual,
              data,
              preguntaColonia
            );

          const coloniaValida =
            listaColonias.includes(
              coloniaActual
            );

          actualizarRespuesta(
            preguntaColonia,
            coloniaValida
              ? coloniaActual
              : listaColonias[0]
          );

        }

        /*
        ================================================
        Ciudad
        ================================================
        */

        if (preguntaCiudad) {

          actualizarRespuesta(
            preguntaCiudad,
            ciudad
          );

        }

        /*
        ================================================
        Estado
        ================================================
        */

        if (preguntaEstado) {

          actualizarRespuesta(
            preguntaEstado,
            estado
          );

        }

        /*
        ================================================
        País
        ================================================
        */

        if (preguntaPais) {

          actualizarRespuesta(
            preguntaPais,
            "México"
          );

        }

      } catch (error) {

        console.error(
          "Error buscando código postal:",
          error
        );

        setColonias([]);

      }

    };

  /*
  ========================================================
  ACTUALIZAR CÓDIGO DE PAÍS DE TELÉFONO
  ========================================================
  */

  const actualizarCodigoPaisTelefono =
    (
      pregunta,
      codigoPais
    ) => {

      setGuardado(false);

      setData(
        prev => ({

          ...prev,

          ...(pregunta.campo === "whatsapp"
            ? {
                whatsappCountryCode:
                  codigoPais
              }
            : {}),

          respuestas: {

            ...(prev.respuestas || {}),

            [`${pregunta.id}_countryCode`]:
              codigoPais

          }

        })
      );

    };  

  /*
  ========================================================
   ESPECIFICACIÓN
  ========================================================
  */

  const actualizarEspecificacion =
    (
      pregunta,
      valor
    ) => {

      setGuardado(false);

      setFirmaTieneContenido(false);

      if (firmaCanvasRef.current) {
        const canvas = firmaCanvasRef.current;
        const ctx = canvas.getContext("2d");

        ctx.clearRect(
          0,
          0,
          canvas.width,
          canvas.height
        );
      }

      setData(
        prev => ({

          ...prev,

          respuestas: {

            ...(prev.respuestas || {}),

            [`${pregunta.id}_especificacion`]:
              valor

          }

        })
      );

    };


  /*
  ========================================================
   GUARDAR
  ========================================================
  */

  const guardarBorrador = async () => {
    if (!clinicId || !idPaciente || guardando) return;

    try {
      setGuardando(true);
      setGuardado(false);

      await setDoc(
        doc(db, "clinics", clinicId, "pacientes", idPaciente),
        {
          historiaClinica: {
            ...data,
            actualizadoEn: new Date().toISOString()
          }
        },
        { merge: true }
      );

      setGuardado(true);
      notify({
        type: "success",
        title: "Borrador guardado",
        message: "Puedes continuar completando la historia clínica cuando quieras."
      });
    } catch (error) {
      console.error("Error guardando borrador de historia clínica:", error);
      setGuardado(false);
      notify({
        type: "error",
        title: "No se pudo guardar el borrador",
        message: "Verifica tu conexión e inténtalo nuevamente."
      });
    } finally {
      setGuardando(false);
    }
  };

  const guardar =
    async () => {

      if (
        !clinicId ||
        !idPaciente
      ) {

        return;

      }


      /*
      ========================================================
      LA FIRMA ES OBLIGATORIA PARA CREAR UNA VERSIÓN OFICIAL
      ========================================================
      */

      if (
        !firmaTieneContenido ||
        !firmaCanvasRef.current
      ) {

        notify({

          type: "error",

          title:
            "Firma requerida",

          message:
            "Para guardar una versión oficial de la historia clínica, el paciente debe firmar."

        });

        return;

      }

      if (!clinicData) {

        notify({

          type: "error",

          title:
            "Consultorio no disponible",

          message:
            "No se pudieron cargar los datos del consultorio. Intenta nuevamente."

        });

        return;

      }

      try {

        setGuardando(true);


        const pacienteRef =
          doc(
            db,
            "clinics",
            clinicId,
            "pacientes",
            idPaciente
          );


        /*
        ========================================================
        OBTENEMOS LA FIRMA EXACTAMENTE COMO FUE REALIZADA
        ========================================================
        */

        const firmaDataUrl =
          firmaCanvasRef.current.toDataURL(
            "image/png"
          );


        /*
        ========================================================
        CONSTRUIMOS LOS CAMPOS DEL PACIENTE

        Esto conserva exactamente el comportamiento anterior.
        ========================================================
        */

        const camposActualizar =
          {};


        configuracionHistoria
          .secciones
          .forEach(
            seccionConfig => {

              (
                seccionConfig
                  .preguntas ||
                []
              ).forEach(
                pregunta => {

                  if (
                    !pregunta.campo
                  ) {

                    return;

                  }


                  const tieneRespuesta =
                    Object.prototype.hasOwnProperty.call(
                      data.respuestas || {},
                      pregunta.id
                    );


                  if (
                    !tieneRespuesta
                  ) {

                    return;

                  }


                  const valorRespuesta =
                    data.respuestas[
                      pregunta.id
                    ];


                  /*
                  ========================================================
                  WHATSAPP

                  Conservamos exactamente el formato interno actual.
                  ========================================================
                  */

                  if (
                    pregunta.campo === "whatsapp"
                  ) {

                    const codigoPais =
                      data.respuestas[
                        `${pregunta.id}_countryCode`
                      ] ||
                      pacienteActual?.whatsappCountryCode ||
                      "+52";


                    const prefijoWhatsApp =
                      obtenerPrefijoWhatsApp(
                        codigoPais
                      );


                    const numero =
                      String(
                        valorRespuesta ?? ""
                      ).replace(
                        /\D/g,
                        ""
                      );


                    const numeroSinPrefijo =
                      numero.startsWith(
                        prefijoWhatsApp
                      )
                        ? numero.substring(
                            prefijoWhatsApp.length
                          )
                        : numero;


                    camposActualizar.whatsapp =
                      numeroSinPrefijo
                        ? prefijoWhatsApp +
                          numeroSinPrefijo
                        : "";


                    camposActualizar.whatsappCountryCode =
                      codigoPais;

                  } else {

                    camposActualizar[
                      pregunta.campo
                    ] =
                      valorRespuesta;

                  }

                }
              );

            }
          );


        /*
        ========================================================
        BUSCAMOS LAS VERSIONES OFICIALES EXISTENTES
        ========================================================
        */

        const historiasFirmadasRef =
          collection(
            db,
            "clinics",
            clinicId,
            "pacientes",
            idPaciente,
            "historiasClinicasFirmadas"
          );


        const historiasFirmadasSnapshot =
          await getDocs(
            historiasFirmadasRef
          );


        const siguienteVersion =
          historiasFirmadasSnapshot.size + 1;


        /*
        ========================================================
        ID ÚNICO DE ESTA VERSIÓN
        ========================================================
        */

        const versionId =
          `${Date.now()}_${siguienteVersion}`;


        const versionRef =
          doc(
            historiasFirmadasRef,
            versionId
          );


        /*
        ========================================================
        FECHA/HORA DE LA FIRMA

        Se almacena como texto ISO para conservar
        exactamente el momento de creación.
        ========================================================
        */

        const fechaFirma =
          new Date().toISOString();


        /*
        ========================================================
        SNAPSHOT INMUTABLE

        Aquí guardamos una COPIA completa de la historia
        tal como estaba en el momento exacto de la firma.
        ========================================================
        */

        const historiaFirmada = {

          version:
            siguienteVersion,

          versionId,

          clinicId,

          pacienteId:
            idPaciente,

          fechaFirma,

          consultorio: {

            nombreClinica:
              clinicData?.nombreClinica || "",

            telefono:
              clinicData?.telefono || "",

            email:
              clinicData?.email || "",

            RFC:
              clinicData?.RFC || "",

            direccion:
              clinicData?.direccion || null,

            logoURL:
              clinicData?.logoURL || "",

            logoBase64:
              clinicData?.logoBase64 || ""

          },

          configuracionHistoria: {

            ...configuracionHistoria,

            secciones:
              (
                configuracionHistoria?.secciones ||
                []
              ).map(
                seccion => ({

                  ...seccion,

                  preguntas:
                    (
                      seccion.preguntas ||
                      []
                    ).map(
                      pregunta => ({
                        ...pregunta
                      })
                    )

                })
              )

          },
          
          paciente: {

            id:
              pacienteActual?.id ||
              idPaciente,

            nombre:
              pacienteActual?.nombre || "",

            apellidos:
              pacienteActual?.apellidos || "",

            fechaNacimiento:
              pacienteActual?.fechaNacimiento || "",

            sexo:
              pacienteActual?.sexo || "",

            curp:
              pacienteActual?.curp || "",

            rfc:
              pacienteActual?.rfc || ""

          },          

          historiaClinica: {

            ...data,

            respuestas: {

              ...(data.respuestas || {})

            }

          },

          camposPaciente: {

            ...camposActualizar

          },

          firma: {

            tipo: "canvas",

            imagen:
              firmaDataUrl

          }

        };


        /*
        ========================================================
        ESCRITURA ATÓMICA

        1. Guarda la versión firmada.
        2. Actualiza la historia actual del paciente.

        Ambas operaciones se ejecutan juntas.
        ========================================================
        */

        const batch =
          writeBatch(db);


        batch.set(
          versionRef,
          historiaFirmada
        );


        batch.set(
          pacienteRef,
          {

            ...camposActualizar,

            historiaClinica: {

              ...data,

              respuestas: {

                ...(data.respuestas || {})

              }

            }

          },
          {
            merge: true
          }
        );


        await batch.commit();


        /*
        ========================================================
        ACTUALIZAMOS EL OBJETO LOCAL DEL PACIENTE
        ========================================================
        */

        setPacienteActual(
          prev => ({

            ...(prev || {}),

            ...camposActualizar

          })
        );


        /*
        ========================================================
        GUARDADO CORRECTO
        ========================================================
        */

        setGuardado(true);


        /*
        ========================================================
        LA FIRMA YA FUE UTILIZADA.

        La limpiamos para evitar reutilizar accidentalmente
        la misma firma para otra modificación.
        ========================================================
        */

        setFirmaTieneContenido(false);


        if (
          firmaCanvasRef.current
        ) {

          const canvas =
            firmaCanvasRef.current;

          const ctx =
            canvas.getContext("2d");

          ctx.clearRect(
            0,
            0,
            canvas.width,
            canvas.height
          );

        }


        notify({

          type: "success",

          title:
            "Historia clínica guardada",

          message:
            `La historia clínica se guardó como versión oficial #${siguienteVersion}.`

        });


      } catch (error) {

        console.error(
          "Error guardando historia clínica firmada:",
          error
        );


        notify({

          type: "error",

          title:
            "Error al guardar",

          message:
            "No se pudo guardar la historia clínica firmada."

        });


      } finally {

        setGuardando(false);

      }

    };

          /*
          ========================================================
          ABRIR HISTORIA CLÍNICA FIRMADA
          ========================================================
          */

          const abrirHistoriaFirmada =
            (historia) => {

              if (!historia) {
                return;
              }


              try {

                const pdf =
                  new jsPDF({
                    orientation: "portrait",
                    unit: "mm",
                    format: "letter"
                  });


                /*
                ========================================================
                CONFIGURACIÓN DEL DOCUMENTO
                ========================================================
                */

                const margen =
                  20;

                const anchoPagina =
                  pdf.internal.pageSize.getWidth();

                const altoPagina =
                  pdf.internal.pageSize.getHeight();

                const anchoContenido =
                  anchoPagina -
                  margen * 2;


                let y =
                  20;


                /*
                ========================================================
                FUNCIONES AUXILIARES
                ========================================================
                */

                const nuevaPagina =
                  () => {

                    pdf.addPage();

                    y = 20;

                  };


                const comprobarEspacio =
                  (alturaNecesaria) => {

                    if (
                      y + alturaNecesaria >
                      altoPagina - 20
                    ) {

                      nuevaPagina();

                    }

                  };


                const escribirTexto =
                  (
                    texto,
                    opciones = {}
                  ) => {

                    const {

                      fontSize = 10,

                      fontStyle = "normal",

                      espacioAntes = 0,

                      espacioDespues = 4,

                      maxWidth =
                        anchoContenido

                    } = opciones;


                    pdf.setFont(
                      "helvetica",
                      fontStyle
                    );

                    pdf.setFontSize(
                      fontSize
                    );


                    const lineas =
                      pdf.splitTextToSize(
                        String(
                          texto ??
                          ""
                        ),
                        maxWidth
                      );


                    const alturaLinea =
                      fontSize *
                      0.45;


                    comprobarEspacio(
                      lineas.length *
                        alturaLinea +
                        espacioAntes +
                        espacioDespues
                    );


                    y +=
                      espacioAntes;


                    pdf.text(
                      lineas,
                      margen,
                      y
                    );


                    y +=
                      lineas.length *
                      alturaLinea +
                      espacioDespues;

                  };


                const escribirSeccion =
                  (titulo) => {

                    comprobarEspacio(
                      16
                    );


                    pdf.setFillColor(
                      241,
                      245,
                      249
                    );


                    pdf.roundedRect(
                      margen,
                      y - 5,
                      anchoContenido,
                      10,
                      2,
                      2,
                      "F"
                    );


                    pdf.setFont(
                      "helvetica",
                      "bold"
                    );

                    pdf.setFontSize(
                      11
                    );


                    pdf.setTextColor(
                      15,
                      23,
                      42
                    );


                    pdf.text(
                      titulo,
                      margen + 4,
                      y + 2
                    );


                    y += 12;


                    pdf.setTextColor(
                      0,
                      0,
                      0
                    );

                  };


                /*
                ========================================================
                ENCABEZADO DEL CONSULTORIO
                ========================================================
                */

                const logo =
                  historia.consultorio?.logoBase64 ||
                  historia.consultorio?.logoURL ||
                  "";


                if (logo) {

                  try {

                    comprobarEspacio(
                      28
                    );


                    pdf.addImage(
                      logo,
                      "PNG",
                      margen,
                      y,
                      35,
                      22
                    );

                    y += 27;

                  } catch (error) {

                    console.warn(
                      "No se pudo insertar el logo histórico:",
                      error
                    );

                  }

                }


                escribirTexto(
                  historia.consultorio?.nombreClinica ||
                    "Consultorio dental",
                  {
                    fontSize: 16,
                    fontStyle: "bold",
                    espacioDespues: 3
                  }
                );


                const datosConsultorio =
                  [];


                if (
                  historia.consultorio?.telefono
                ) {

                  datosConsultorio.push(
                    `Teléfono: ${historia.consultorio.telefono}`
                  );

                }


                if (
                  historia.consultorio?.email
                ) {

                  datosConsultorio.push(
                    `Correo: ${historia.consultorio.email}`
                  );

                }


                if (
                  historia.consultorio?.RFC
                ) {

                  datosConsultorio.push(
                    `RFC: ${historia.consultorio.RFC}`
                  );

                }


                if (
                  historia.consultorio?.direccion
                ) {

                  if (
                    typeof historia.consultorio.direccion ===
                    "string"
                  ) {

                    datosConsultorio.push(
                      `Dirección: ${historia.consultorio.direccion}`
                    );

                  }

                }


                datosConsultorio.forEach(
                  dato => {

                    escribirTexto(
                      dato,
                      {
                        fontSize: 9,
                        espacioDespues: 2
                      }
                    );

                  }
                );


                y += 4;


                /*
                ========================================================
                TÍTULO
                ========================================================
                */

                escribirTexto(
                  "HISTORIA CLÍNICA",
                  {
                    fontSize: 20,
                    fontStyle: "bold",
                    espacioAntes: 5,
                    espacioDespues: 4
                  }
                );


                escribirTexto(
                  "VERSIÓN OFICIAL",
                  {
                    fontSize: 10,
                    fontStyle: "bold",
                    espacioDespues: 2
                  }
                );


                escribirTexto(
                  `Versión: #${
                    historia.version ??
                    ""
                  }`,
                  {
                    fontSize: 9,
                    espacioDespues: 2
                  }
                );


                if (
                  historia.fechaFirma
                ) {

                  escribirTexto(
                    `Fecha y hora de firma: ${new Date(
                      historia.fechaFirma
                    ).toLocaleString(
                      "es-MX"
                    )}`,
                    {
                      fontSize: 9,
                      espacioDespues: 6
                    }
                  );

                }


                /*
                ========================================================
                DATOS DEL PACIENTE
                ========================================================
                */

                escribirSeccion(
                  "DATOS DEL PACIENTE"
                );


                const nombrePaciente =
                  [
                    historia.paciente?.nombre,
                    historia.paciente?.apellidos
                  ]
                    .filter(Boolean)
                    .join(" ");


                escribirTexto(
                  `Nombre: ${
                    nombrePaciente ||
                    "No registrado"
                  }`,
                  {
                    fontSize: 10,
                    espacioDespues: 3
                  }
                );


                if (
                  historia.paciente?.fechaNacimiento
                ) {

                  escribirTexto(
                    `Fecha de nacimiento: ${
                      historia.paciente.fechaNacimiento
                    }`,
                    {
                      fontSize: 10,
                      espacioDespues: 3
                    }
                  );

                }


                if (
                  historia.paciente?.sexo
                ) {

                  escribirTexto(
                    `Sexo: ${
                      historia.paciente.sexo
                    }`,
                    {
                      fontSize: 10,
                      espacioDespues: 3
                    }
                  );

                }


                if (
                  historia.paciente?.curp
                ) {

                  escribirTexto(
                    `CURP: ${
                      historia.paciente.curp
                    }`,
                    {
                      fontSize: 10,
                      espacioDespues: 3
                    }
                  );

                }


                if (
                  historia.paciente?.rfc
                ) {

                  escribirTexto(
                    `RFC: ${
                      historia.paciente.rfc
                    }`,
                    {
                      fontSize: 10,
                      espacioDespues: 6
                    }
                  );

                }


                /*
                ========================================================
                HISTORIA CLÍNICA
                ========================================================
                */

                const configuracion =
                  historia.configuracionHistoria;


                const respuestas =
                  historia.historiaClinica?.respuestas ||
                  {};


                const secciones =
                  configuracion?.secciones ||
                  [];


                secciones.forEach(
                  seccion => {

                    escribirSeccion(
                      String(
                        seccion.titulo ||
                        seccion.id ||
                        "Sección"
                      ).toUpperCase()
                    );


                    const preguntas =
                      seccion.preguntas ||
                      [];


                    preguntas.forEach(
                      pregunta => {

                        const valor =
                          obtenerValorPaciente(
                            historia.paciente,
                            historia.historiaClinica,
                            pregunta
                          );


                        const especificacion =
                          respuestas[
                            `${pregunta.id}_especificacion`
                          ];


                        let respuestaTexto =
                          "";


                        if (
                          Array.isArray(
                            valor
                          )
                        ) {

                          respuestaTexto =
                            valor.join(
                              ", "
                            );

                        } else if (
                          typeof valor ===
                          "boolean"
                        ) {

                          respuestaTexto =
                            valor
                              ? "Sí"
                              : "No";

                        } else {

                          respuestaTexto =
                            valor === null ||
                            valor === undefined ||
                            valor === ""
                              ? "Sin respuesta"
                              : String(
                                  valor
                                );

                        }


                        escribirTexto(
                          String(
                            pregunta.texto ||
                            pregunta.id ||
                            "Pregunta"
                          ),
                          {
                            fontSize: 9,
                            fontStyle: "bold",
                            espacioAntes: 2,
                            espacioDespues: 1
                          }
                        );


                        escribirTexto(
                          respuestaTexto,
                          {
                            fontSize: 10,
                            espacioDespues: 2
                          }
                        );


                        if (
                          especificacion
                        ) {

                          escribirTexto(
                            `Especificación: ${especificacion}`,
                            {
                              fontSize: 9,
                              espacioDespues: 3
                            }
                          );

                        }

                      }
                    );

                  }
                );


                /*
                ========================================================
                FIRMA DEL PACIENTE
                ========================================================
                */

                comprobarEspacio(
                  75
                );


                escribirSeccion(
                  "FIRMA DEL PACIENTE"
                );


                if (
                  historia.firma?.imagen
                ) {

                  try {

                    pdf.addImage(
                      historia.firma.imagen,
                      "PNG",
                      margen,
                      y,
                      100,
                      33
                    );

                    y += 38;

                  } catch (error) {

                    console.warn(
                      "No se pudo insertar la firma histórica:",
                      error
                    );

                  }

                }


                escribirTexto(
                  "Firma digital del paciente",
                  {
                    fontSize: 9,
                    espacioDespues: 3
                  }
                );


                escribirTexto(
                  "Esta versión corresponde al estado de la historia clínica al momento de la firma y no es editable.",
                  {
                    fontSize: 8,
                    espacioAntes: 4,
                    espacioDespues: 2
                  }
                );


                /*
                ========================================================
                PIE DE DOCUMENTO
                ========================================================
                */

                const totalPaginas =
                  pdf.internal.getNumberOfPages();


                for (
                  let pagina = 1;
                  pagina <= totalPaginas;
                  pagina++
                ) {

                  pdf.setPage(
                    pagina
                  );


                  pdf.setFont(
                    "helvetica",
                    "normal"
                  );

                  pdf.setFontSize(
                    8
                  );

                  pdf.setTextColor(
                    100,
                    116,
                    139
                  );


                  pdf.text(
                    `Historia clínica — Versión oficial #${
                      historia.version ??
                      ""
                    }`,
                    margen,
                    altoPagina - 10
                  );


                  pdf.text(
                    `Página ${pagina} de ${totalPaginas}`,
                    anchoPagina - margen,
                    altoPagina - 10,
                    {
                      align: "right"
                    }
                  );

                }


                /*
                ========================================================
                ABRIR EN EL VISOR INTERNO DE VEXADENT
                ========================================================
                */

                const blob =
                  pdf.output(
                    "blob"
                  );


                const url =
                  URL.createObjectURL(
                    blob
                  );


                setHistoriaSeleccionada(
                  historia
                );


                setHistoriaViewerUrl(
                  url
                );


                setHistoriaViewerOpen(
                  true
                );


              } catch (error) {

                console.error(
                  "Error generando historia clínica firmada:",
                  error
                );


                notify({

                  type: "error",

                  title:
                    "No se pudo abrir la historia",

                  message:
                    "No fue posible generar el documento histórico."

                });

              }

            };

  /*
  ========================================================
  GUARDADO MANUAL

  La historia clínica se guarda únicamente
  al presionar el botón flotante Guardar.
  ========================================================
  */


  /*
  ========================================================
   LOADING
  ========================================================
  */

  if (loading) {

    return (

      <div style={styles.loading}>

        Cargando historia clínica...

      </div>

    );

  }


  /*
  ========================================================
   PACIENTE NO ENCONTRADO
  ========================================================
  */

  if (!idPaciente) {

    return (

      <div style={styles.empty}>

        No se encontró el paciente.

      </div>

    );

  }


  /*
  ========================================================
   RENDER
  ========================================================
  */

  return (

    <div className="vd-history-page" style={styles.container}>

      {/* ENCABEZADO */}

      <section className="vd-history-hero">
        <div className="vd-history-hero__main">
          <div className="vd-history-hero__icon"><ClipboardList size={25} /></div>
          <div>
            <span className="vd-history-hero__eyebrow">Expediente clínico</span>
            <h2>Historia clínica</h2>
            <p>Captura, consulta y conserva la información del paciente en un solo flujo.</p>
            <div className="vd-history-hero__patient"><UserRound size={14} /> {pacienteActual?.nombre || ""} {pacienteActual?.apellidos || ""}</div>
          </div>
        </div>
        <div className="vd-history-hero__stats">
          <div><strong>{porcentajeCompletado}%</strong><span>completado</span></div>
          <div><strong>{historiasFirmadas.length}</strong><span>versiones oficiales</span></div>
          <div className={guardado ? "is-saved" : "is-pending"}><strong>{guardando ? "…" : guardado ? "✓" : "!"}</strong><span>{guardando ? "guardando" : guardado ? "sincronizado" : "pendiente"}</span></div>
        </div>
      </section>

      <section className="vd-history-progress" aria-label="Progreso de la historia clínica">
        <div className="vd-history-progress__copy">
          <div><Sparkles size={16} /><strong>Avance del expediente</strong></div>
          <span>{preguntasRespondidas} de {preguntasTotales} campos respondidos{preguntasPendientes ? ` · ${preguntasPendientes} pendientes` : " · Historia completa"}</span>
        </div>
        <div className="vd-history-progress__track"><i style={{ width: `${porcentajeCompletado}%` }} /></div>
      </section>


      {/* ================================================
          LOS 4 BOTONES
      ================================================= */}

      <div className="vd-history-tabs" style={styles.tabs}>

        {SECCIONES_FIJAS.map(
          tab => {

            const existe =
              secciones.some(
                s =>
                  s.id ===
                  tab.id
              );


            return (

              <button

                key={
                  tab.id
                }

                type="button"

                onClick={() =>
                  setSeccionActiva(
                    tab.id
                  )
                }

                className={`vd-history-tab ${seccionActiva === tab.id ? "is-active" : ""}`}
                style={{

                  ...styles.tab,

                  ...(seccionActiva ===
                    tab.id
                    ? styles.tabActive
                    : {}),

                  opacity:
                    existe
                      ? 1
                      : 0.6

                }}

              >

                {tab.titulo}

              </button>

            );

          }
        )}

              {/* FIRMA DIGITAL */}

                <button
                  type="button"
                  onClick={() =>
                    setSeccionActiva(
                      "firmaDigital"
                    )
                  }
                  className={`vd-history-tab ${seccionActiva === "firmaDigital" ? "is-active" : ""}`}
                  style={{
                    ...styles.tab,

                    ...(seccionActiva ===
                      "firmaDigital"
                      ? styles.tabActive
                      : {})
                  }}
                >
                  Firma digital
                </button>

      </div>

      {/* ================================================
          CONTENIDO DE LA SECCIÓN
      ================================================= */}

      {seccionActiva ===
        "firmaDigital" ? (

        <>

          <div className="vd-history-card" style={
            styles.sectionCard
          }>

            <div className="vd-history-section-heading" style={
              styles.sectionTitle
            }>
              Firma digital
            </div>

            <div
              style={{
                padding: 24,
                background: "#ffffff",
                borderRadius: 16,
                border: "1px solid #e2e8f0"
              }}
            >

              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 10,
                  color: "#334155"
                }}
              >
                Firma del paciente
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  marginBottom: 14
                }}
              >
                Firme dentro del recuadro utilizando el dedo,
                mouse o stylus.
              </div>

              <canvas
                ref={firmaCanvasRef}

                onPointerDown={
                  iniciarFirma
                }

                onPointerMove={
                  dibujarFirma
                }

                onPointerUp={
                  terminarFirma
                }

                onPointerCancel={
                  terminarFirma
                }

                width={900}
                height={300}

                style={{
                  width: "100%",
                  maxWidth: 900,
                  height: 300,
                  display: "block",

                  background: "#ffffff",

                  border:
                    "2px dashed #cbd5e1",

                  borderRadius: 12,

                  touchAction: "none",

                  cursor: "crosshair"
                }}
              />

              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  justifyContent: "flex-end"
                }}
              >

                <button
                  type="button"
                  onClick={() => {

                    const canvas =
                      firmaCanvasRef.current;

                    if (!canvas) {
                      return;
                    }

                    const ctx =
                      canvas.getContext("2d");

                    ctx.clearRect(
                      0,
                      0,
                      canvas.width,
                      canvas.height
                    );

                    setFirmaTieneContenido(
                      false
                    );

                  }}
                  style={{
                    padding:
                      "9px 16px",

                    border:
                      "1px solid #cbd5e1",

                    borderRadius: 10,

                    background:
                      "#ffffff",

                    color:
                      "#334155",

                    fontWeight: 600,

                    cursor:
                      "pointer"
                  }}
                >
                  Limpiar firma
                </button>             

              </div>

            </div>

          </div>

          {/* =====================================================
              REGISTRO DE HISTORIAS CLÍNICAS FIRMADAS
          ====================================================== */}

          <div
            style={{
              marginTop: 24,
              padding: 24,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              boxShadow:
                "0 2px 8px rgba(15, 23, 42, 0.04)"
            }}
          >

            <div
              style={{
                marginBottom: 18
              }}
            >

              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0f172a"
                }}
              >
                Registro de historias clínicas
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: "#64748b"
                }}
              >
                Historial de versiones oficiales
                firmadas por el paciente.
              </div>

            </div>


            {cargandoHistoriasFirmadas ? (

              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: 14
                }}
              >
                Cargando historial...
              </div>

            ) : historiasFirmadas.length === 0 ? (

              <div
                style={{
                  padding: 24,
                  border:
                    "1px dashed #cbd5e1",
                  borderRadius: 12,
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 14,
                  textAlign: "center"
                }}
              >
                Aún no existen versiones oficiales
                de esta historia clínica.
              </div>

            ) : (

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10
                }}
              >

                {historiasFirmadas.map(
                  historia => (

                    <div
                      key={historia.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "space-between",
                        gap: 16,
                        padding:
                          "16px 18px",
                        border:
                          "1px solid #e2e8f0",
                        borderRadius: 12,
                        background:
                          "#ffffff"
                      }}
                    >

                      <div>

                        <div
                          style={{
                            fontWeight: 700,
                            color: "#0f172a"
                          }}
                        >
                          Versión #
                          {historia.version}
                        </div>

                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 13,
                            color: "#64748b"
                          }}
                        >
                          {historia.fechaFirma
                            ? new Date(
                                historia.fechaFirma
                              ).toLocaleString(
                                "es-MX"
                              )
                            : "Fecha no disponible"}
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#15803d"
                          }}
                        >
                          ✓ Versión oficial firmada
                        </div>

                      </div>


                      <button
                        type="button"
                        onClick={() =>
                          abrirHistoriaFirmada(
                            historia
                          )
                        }
                        style={{
                          padding:
                            "9px 14px",
                          border:
                            "1px solid #2563eb",
                          borderRadius: 10,
                          background:
                            "#ffffff",
                          color:
                            "#2563eb",
                          fontWeight: 700,
                          cursor:
                            "pointer",
                          whiteSpace:
                            "nowrap"
                        }}
                      >
                        Ver historia
                      </button>

                    </div>

                  )
                )}

              </div>

            )}

          </div>

          </>

        ) : seccion ? (

        <div className="vd-history-card" style={
          styles.sectionCard
        }>

          <div className="vd-history-section-heading" style={
            styles.sectionTitle
          }>
            <div>
              <span>SECCIÓN {Math.max(1, secciones.findIndex(item => item.id === seccion.id) + 1)} DE {Math.max(1, secciones.length)}</span>
              <strong>{seccion.titulo}</strong>
              <p>Responde los campos del expediente y guarda un borrador cuando quieras continuar después.</p>
            </div>
            <div className="vd-history-section-heading__count">{seccion.preguntas?.length || 0} campos</div>

          </div>


          {(
            seccion.preguntas ||
            []
          ).length === 0 ? (

            <div style={
              styles.noQuestions
            }>

              No hay preguntas configuradas
              en esta sección.

            </div>

          ) : (

            <div className="vd-history-questions" style={
              styles.questions
            }>

              {seccion.preguntas.map(
                (pregunta, index) => (

                  <PreguntaPaciente

                    key={
                      pregunta.id
                    }

                    numero={
                      index + 1
                    }

                    pregunta={
                      pregunta
                    }

                    paciente={
                      pacienteActual
                    }

                    data={
                      data
                    }

                    onChange={
                      actualizarRespuesta
                    }

                    onCountryCodeChange={
                      actualizarCodigoPaisTelefono
                    }

                    onPostalCodeChange={
                      buscarCodigoPostal
                    }

                    colonias={
                      colonias
                    }

                    onSpecificationChange={
                      actualizarEspecificacion
                    }

                  />

                )
              )}

            </div>

          )}

        </div>

      ) : (

        <div style={
          styles.noQuestions
        }>

          No hay configuración de historia
          clínica disponible.

        </div>

      )}

      {/* ========================================================
          VISOR DE HISTORIA CLÍNICA FIRMADA
      ======================================================== */}

      <FileViewerModal

        isOpen={
          historiaViewerOpen
        }

        onClose={() => {

          setHistoriaViewerOpen(
            false
          );


          if (
            historiaViewerUrl
          ) {

            URL.revokeObjectURL(
              historiaViewerUrl
            );

          }


          setHistoriaViewerUrl(
            ""
          );

          setHistoriaSeleccionada(
            null
          );

        }}

        documentType="HISTORIA CLÍNICA"

        title={
          historiaSeleccionada
            ? `Historia clínica — Versión #${historiaSeleccionada.version}`
            : "Historia clínica"
        }

        subtitle={
          historiaSeleccionada
            ? [
                historiaSeleccionada.paciente?.nombre,
                historiaSeleccionada.paciente?.apellidos
              ]
                .filter(Boolean)
                .join(" ")
            : ""
        }

        date={
          historiaSeleccionada?.fechaFirma
            ? new Date(
                historiaSeleccionada.fechaFirma
              ).toLocaleDateString(
                "es-MX"
              )
            : ""
        }

        fileUrl={
          historiaViewerUrl
        }

        filename={
          historiaSeleccionada
            ? `HistoriaClinica_Version_${historiaSeleccionada.version}.pdf`
            : "HistoriaClinica.pdf"
        }

        mimeType="application/pdf"

      />      

      <div className="vd-history-savebar">
        <div className="vd-history-savebar__hint">
          <ShieldCheck size={18} />
          <div>
            <strong>{guardado ? "Cambios sincronizados" : "Tienes cambios pendientes"}</strong>
            <span>Guarda un borrador o crea una versión oficial con firma digital.</span>
          </div>
        </div>
        <div className="vd-history-savebar__actions">
          <button type="button" className="vd-history-savebar__draft" onClick={guardarBorrador} disabled={guardando}>
            <Save size={16} /> {guardando ? "Guardando..." : "Guardar borrador"}
          </button>
          <button type="button" className="vd-history-savebar__official" onClick={guardar} disabled={guardando}>
            <FileCheck2 size={16} /> Crear versión oficial
          </button>
        </div>
      </div>

    </div>

  );

}

/*
=========================================================
 SUGERENCIAS DE DOMINIO PARA CORREO
=========================================================
*/

const DOMINIOS_CORREO = [
  "gmail.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "yahoo.com",
  "live.com",
  "proton.me",
  "protonmail.com"
];

function obtenerSugerenciasCorreo(valor) {

  const texto = String(
    valor || ""
  ).trim();

  if (!texto.includes("@")) {
    return [];
  }

  const partes =
    texto.split("@");

  if (partes.length !== 2) {
    return [];
  }

  const usuario =
    partes[0];

  const dominioEscrito =
    partes[1].toLowerCase();

  if (!usuario) {
    return [];
  }

  return DOMINIOS_CORREO
    .filter(
      dominio =>
        dominio.startsWith(
          dominioEscrito
        )
    )
    .map(
      dominio =>
        `${usuario}@${dominio}`
    );
}

/*
=========================================================
 PREGUNTA DEL PACIENTE
=========================================================
*/

function obtenerTextoTipoPregunta(tipo) {

  const tipos = {

    texto: "Respuesta abierta",

    texto_largo: "Respuesta amplia",

    si_no: "Sí / No",

    checkbox: "Selección",

    seleccion: "Selección única",

    seleccion_multiple:
      "Selección múltiple",

    numero: "Número",

    fecha: "Fecha",

    telefono: "Teléfono"

  };
  return (
    tipos[tipo] ||
    "Respuesta"
  );

}

function PreguntaPaciente({

  numero,

  pregunta,

  paciente,

  data,

  onChange,

  onCountryCodeChange,

  onPostalCodeChange,

  colonias,

  onSpecificationChange

}) {

  const [
      mostrarSugerenciasCorreo,
      setMostrarSugerenciasCorreo
    ] = useState(false);

  const valorOriginal =
    obtenerValorPaciente(
      paciente,
      data,
      pregunta
    );

  const valor =
    normalizarValorInput(
      valorOriginal,
      pregunta.tipo
    );

  const sugerenciasCorreo =
    pregunta.campo === "correo"
      ? obtenerSugerenciasCorreo(valor)
      : [];

  const especificacion =
    data?.respuestas?.[
      `${pregunta.id}_especificacion`
    ] || "";

  const mostrarEspecificacion =
    pregunta.permitirEspecificar &&
    (
      pregunta.tipo !== "si_no" &&
      pregunta.tipo !== "checkbox"
        ? true
        : valor === true
    );


  return (

  <div style={styles.question}>

    <div style={styles.questionHeader}>

      <div style={styles.questionNumber}>

        {String(numero).padStart(2, "0")}

      </div>

      <div style={styles.questionHeaderContent}>

        <div style={styles.questionLabel}>

          {pregunta.texto}

          {pregunta.obligatorio && (

            <span style={styles.required}>

              *

            </span>

          )}

        </div>

        <div style={styles.questionType}>

          {obtenerTextoTipoPregunta(
            pregunta.tipo
          )}

        </div>

      </div>

    </div>


    <div style={styles.questionAnswer}>

    {/* CÓDIGO POSTAL */}

    {pregunta.campo === "cp" && (

      <input
        type="text"
        inputMode="numeric"
        maxLength={5}
        value={valor ?? ""}
        onChange={e =>
          onPostalCodeChange(
            pregunta,
            e.target.value
          )
        }
        placeholder="Código postal"
        style={styles.input}
      />

    )}


    {/* COLONIA */}

    {pregunta.campo === "colonia" && (

      <select
        value={valor ?? ""}
        onChange={e =>
          onChange(
            pregunta,
            e.target.value
          )
        }
        style={styles.input}
      >

        <option value="">
          Seleccionar colonia
        </option>

        {(colonias || []).map(
          (colonia, index) => (

            <option
              key={`${colonia}-${index}`}
              value={colonia}
            >
              {colonia}
            </option>

          )
        )}

      </select>

    )}


    {/* ESTADO */}

    {pregunta.campo === "estado" && (

      <input
        type="text"
        value={valor ?? ""}
        readOnly
        style={{
          ...styles.input,
          ...styles.readOnly
        }}
      />

    )}


    {/* PAÍS */}

    {pregunta.campo === "pais" && (

      <input
        type="text"
        value={valor ?? ""}
        readOnly
        style={{
          ...styles.input,
          ...styles.readOnly
        }}
      />

    )}

      {/* TEXTO */}

      {/* TELÉFONO */}

        {pregunta.tipo === "telefono" && (

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center"
            }}
          >

            <select
              value={
                data?.respuestas?.[
                  `${pregunta.id}_countryCode`
                ] ||
                (
                  pregunta.campo === "whatsapp"
                    ? paciente?.whatsappCountryCode
                    : "+52"
                ) ||
                "+52"
              }
              onChange={e =>
                onCountryCodeChange(
                  pregunta,
                  e.target.value
                )
              }
              style={{
                ...styles.input,
                width: 145,
                flexShrink: 0
              }}
            >

              <option value="+52">
                🇲🇽 México (+52)
              </option>

              <option value="+1">
                🇺🇸 Estados Unidos (+1)
              </option>

              <option value="+57">
                🇨🇴 Colombia (+57)
              </option>

              <option value="+54">
                🇦🇷 Argentina (+54)
              </option>

              <option value="+56">
                🇨🇱 Chile (+56)
              </option>

              <option value="+51">
                🇵🇪 Perú (+51)
              </option>

              <option value="+34">
                🇪🇸 España (+34)
              </option>

            </select>


            <input
              type="tel"
              value={

                pregunta.campo === "whatsapp"

                  ? (() => {

                      const codigo =
                        data?.respuestas?.[
                          `${pregunta.id}_countryCode`
                        ] ||
                        paciente?.whatsappCountryCode ||
                        "+52";

                      const prefijo =
                        obtenerPrefijoWhatsApp(
                          codigo
                        );

                      const numero =
                        String(
                          valor ?? ""
                        ).replace(
                          /\D/g,
                          ""
                        );

                      return numero.startsWith(
                        prefijo
                      )
                        ? numero.substring(
                            prefijo.length
                          )
                        : numero;

                    })()

                  : (
                      valor ?? ""
                    )

              }
              onChange={e => {

                const numero =
                  e.target.value.replace(
                    /\D/g,
                    ""
                  );

                onChange(
                  pregunta,
                  numero
                );

              }}
              placeholder="Número telefónico"
              style={{
                ...styles.input,
                flex: 1
              }}
            />

          </div>

        )}

      {pregunta.tipo ===
        "texto" &&
        ![
          "cp",
          "colonia",
          "estado",
          "pais"
        ].includes(
          pregunta.campo
        ) && (

        <div
          style={{
            position: "relative"
          }}
        >

          <input

            type="text"

            value={
              valor ?? ""
            }

            readOnly={
              !!pregunta.soloLectura
            }

            onChange={e => {

              onChange(
                pregunta,
                e.target.value
              );

              if (
                pregunta.campo === "correo"
              ) {
                setMostrarSugerenciasCorreo(
                  true
                );
              }

            }}

            onFocus={() => {

              if (
                pregunta.campo === "correo"
              ) {
                setMostrarSugerenciasCorreo(
                  true
                );
              }

            }}

            onBlur={() => {

              if (
                pregunta.campo === "correo"
              ) {
                setMostrarSugerenciasCorreo(
                  false
                );
              }

            }}

            style={{

              ...styles.input,

              ...(pregunta.soloLectura
                ? styles.readOnly
                : {})

            }}

          />


          {pregunta.campo === "correo" &&
            mostrarSugerenciasCorreo &&
            sugerenciasCorreo.length > 0 && (

            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: "100%",
                zIndex: 50,

                background: "#ffffff",

                border:
                  "1px solid #d1d5db",

                borderRadius: 10,

                marginTop: 4,

                boxShadow:
                  "0 8px 20px rgba(0,0,0,0.12)",

                overflow: "hidden"
              }}
            >

              {sugerenciasCorreo.map(
                sugerencia => (

                <button
                  key={sugerencia}
                  type="button"

                  onMouseDown={e =>
                    e.preventDefault()
                  }

                  onClick={() => {

                    onChange(
                      pregunta,
                      sugerencia
                    );

                    setMostrarSugerenciasCorreo(
                      false
                    );

                  }}

                  style={{
                    display: "block",

                    width: "100%",

                    padding:
                      "10px 14px",

                    border: "none",

                    background:
                      "#ffffff",

                    textAlign: "left",

                    cursor: "pointer",

                    fontSize: 14
                  }}
                >

                  {sugerencia}

                </button>

              ))}

            </div>

          )}

        </div>

      )}


      {/* TEXTO LARGO */}

      {pregunta.tipo ===
        "texto_largo" && (

        <textarea

          value={
            valor ?? ""
          }

          onChange={e =>
            onChange(
              pregunta,
              e.target.value
            )
          }

          rows={4}

          style={
            styles.textarea
          }

        />

      )}


      {/* NÚMERO */}

      {pregunta.tipo ===
        "numero" && (

        <input

          type="number"

          value={
            valor ?? ""
          }

          readOnly={
            !!pregunta.soloLectura ||
            pregunta.campo === "edad"
          }

          onChange={e =>
            onChange(
              pregunta,
              e.target.value
            )
          }

          style={{

            ...styles.input,

            ...(pregunta.soloLectura
              ? styles.readOnly
              : {})

          }}

        />

      )}


      {/* FECHA */}

      {pregunta.tipo ===
        "fecha" && (

        <input

          type="date"

          value={
            valor ?? ""
          }

          onChange={e =>
            onChange(
              pregunta,
              e.target.value
            )
          }

          style={
            styles.input
          }

        />

      )}


      {/* SÍ / NO */}

      {pregunta.tipo ===
        "si_no" && (

        <div style={
          styles.choiceRow
        }>

          {[
            {
              value: true,
              label: "Sí"
            },

            {
              value: false,
              label: "No"
            }

          ].map(
            opcion => {

              const activo =
                valor ===
                opcion.value;


              return (

                <button

                  key={
                    opcion.label
                  }

                  type="button"

                  onClick={() =>
                    onChange(
                      pregunta,
                      opcion.value
                    )
                  }

                  style={{

                    ...styles.choiceButton,

                    ...(activo
                      ? styles.choiceActive
                      : {})

                  }}

                >

                  <span>

                    {activo
                      ? "✓ "
                      : ""}

                  </span>

                  {opcion.label}

                </button>

              );

            }
          )}

        </div>

      )}


      {/* CHECKBOX */}

      {pregunta.tipo ===
        "checkbox" && (

        <label style={
          styles.checkboxRow
        }>

          <input

            type="checkbox"

            checked={
              valor === true
            }

            onChange={e =>
              onChange(
                pregunta,
                e.target.checked
              )
            }

          />

          <span>

            Sí

          </span>

        </label>

      )}


      {/* SELECCIÓN ÚNICA */}

      {pregunta.tipo ===
        "seleccion" && (

        <select

          value={
            valor ?? ""
          }

          onChange={e =>
            onChange(
              pregunta,
              e.target.value
            )
          }

          style={
            styles.input
          }

        >

          <option value="">

            Seleccionar...

          </option>


          {(
            pregunta.opciones ||
            []
          ).map(
            opcion => (

              <option

                key={
                  String(opcion)
                }

                value={
                  opcion
                }

              >

                {opcion}

              </option>

            )
          )}

        </select>

      )}


      {/* SELECCIÓN MÚLTIPLE */}

      {pregunta.tipo ===
        "seleccion_multiple" && (

        <div style={
          styles.multiOptions
        }>

          {(
            pregunta.opciones ||
            []
          ).map(
            opcion => {

              const seleccionadas =
                Array.isArray(
                  valor
                )
                  ? valor
                  : [];


              const checked =
                seleccionadas.includes(
                  opcion
                );


              return (

                <label

                  key={
                    String(opcion)
                  }

                  style={{
                    ...styles.multiOption,
                    ...(checked
                      ? styles.multiOptionActive
                      : {})
                  }}

                >

                  <input

                    type="checkbox"

                    checked={
                      checked
                    }

                    onChange={() => {

                      const nuevas =
                        checked

                          ? seleccionadas.filter(
                              item =>
                                item !==
                                opcion
                            )

                          : [
                              ...seleccionadas,
                              opcion
                            ];


                      onChange(
                        pregunta,
                        nuevas
                      );

                    }}

                  />

                  <span>

                    {opcion}

                  </span>

                </label>

              );

            }
          )}

        </div>

      )}


      {/* ESPECIFICACIÓN */}

      {mostrarEspecificacion && (

        <div style={
          styles.specification
        }>

          <label style={
            styles.specificationLabel
          }>

            {pregunta.especificacionLabel ||
              "Especifique"}

          </label>


          <input

            type="text"

            value={
              especificacion
            }

            onChange={e =>
              onSpecificationChange(
                pregunta,
                e.target.value
              )
            }

            style={
              styles.input
            }

          />

        </div>

      )}

    </div>

  </div>

);

}

/*
=========================================================
 ESTILOS
=========================================================
*/

const styles = {

  container: {

    width: "100%",

    display: "flex",

    flexDirection:
      "column",

    gap: 18

  },


  loading: {

    padding: 40,

    textAlign: "center",

    color: "#64748b"

  },


  empty: {

    padding: 30,

    textAlign: "center",

    color: "#64748b",

    background: "#fff",

    borderRadius: 18

  },


  header: {

    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    gap: 15,

    flexWrap: "wrap"

  },


  title: {

    margin: 0,

    fontSize: 26,

    fontWeight: 800,

    color: "#111827"

  },


  patientName: {

    marginTop: 4,

    color: "#64748b",

    fontSize: 14

  },


  saveStatus: {

    fontSize: 13,

    fontWeight: 700,

    color: "#64748b"

  },


  tabs: {

    display: "flex",

    gap: 10,

    flexWrap: "wrap",

    borderBottom:
      "1px solid #e5e7eb",

    paddingBottom: 12

  },


  tab: {

    padding:
      "11px 18px",

    border: "none",

    borderRadius: 12,

    background: "#e5e7eb",

    color: "#374151",

    cursor: "pointer",

    fontWeight: 700,

    fontSize: 14

  },


  tabActive: {

    background: "#2563eb",

    color: "#fff"

  },


  sectionCard: {

    background: "#fff",

    borderRadius: 20,

    padding: 20,

    boxShadow:
      "0 6px 20px rgba(0,0,0,.06)"

  },


  sectionTitle: {

    fontSize: 21,

    fontWeight: 800,

    color: "#111827",

    marginBottom: 20

  },


  questions: {

    display: "flex",

    flexDirection:
      "column",

    gap: 16

  },


  question: {

    position: "relative",

    padding: 0,

    border:
      "1px solid #e2e8f0",

    borderRadius: 18,

    background: "#ffffff",

    boxShadow:
      "0 4px 14px rgba(15, 23, 42, 0.045)",

    overflow: "visible",

    transition:
      "box-shadow 0.2s ease, transform 0.2s ease"

  },


  questionHeader: {

    display: "flex",

    alignItems: "center",

    gap: 14,

    padding:
      "16px 18px 13px",

    background:
      "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",

    borderBottom:
      "1px solid #eef2f7"

  },


  questionNumber: {

    flexShrink: 0,

    width: 38,

    height: 38,

    borderRadius: 12,

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    background: "#eff6ff",

    color: "#2563eb",

    fontSize: 12,

    fontWeight: 800,

    letterSpacing: "0.04em"

  },


  questionHeaderContent: {

    flex: 1,

    minWidth: 0

  },


  questionLabel: {

    fontSize: 15,

    fontWeight: 750,

    color: "#172033",

    lineHeight: 1.4

  },


  questionType: {

    marginTop: 3,

    fontSize: 11,

    fontWeight: 600,

    color: "#94a3b8",

    letterSpacing: "0.01em"

  },


  required: {

    color: "#dc2626",

    marginLeft: 4,

    fontSize: 15

  },


  questionAnswer: {

    padding: "15px 18px 18px"

  },


  input: {

    width: "100%",

    boxSizing:
      "border-box",

    padding:
      "11px 12px",

    borderRadius: 10,

    border:
      "1px solid #d1d5db",

    fontSize: 14,

    background: "#fff",

    color: "#111827",

    outline: "none"

  },


  readOnly: {

    background: "#f8fafc",

    color: "#64748b"

  },


  textarea: {

    width: "100%",

    boxSizing:
      "border-box",

    minHeight: 110,

    padding: 12,

    borderRadius: 10,

    border:
      "1px solid #d1d5db",

    fontSize: 14,

    resize: "vertical",

    outline: "none"

  },


  choiceRow: {

    display: "grid",

    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",

    gap: 10,

    maxWidth: 360

  },


  choiceButton: {

    minHeight: 48,

    padding:
      "10px 18px",

    borderRadius: 12,

    border:
      "1px solid #dbe2ea",

    background: "#fff",

    color: "#475569",

    cursor: "pointer",

    fontWeight: 750,

    fontSize: 14,

    transition:
      "all 0.15s ease"

  },


  choiceActive: {

    background: "#eff6ff",

    border:
      "2px solid #2563eb",

    color: "#1d4ed8",

    boxShadow:
      "0 2px 8px rgba(37, 99, 235, 0.12)"

  },


  checkboxRow: {

    display: "flex",

    alignItems: "center",

    gap: 9,

    cursor: "pointer",

    fontSize: 14,

    color: "#374151"

  },


  multiOptions: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",

    gap: 10

  },


  multiOption: {

    display: "flex",

    alignItems: "center",

    gap: 10,

    padding:
      "12px 14px",

    border:
      "1px solid #e2e8f0",

    borderRadius: 12,

    background: "#fff",

    cursor: "pointer",

    fontSize: 14,

    color: "#334155",

    transition:
      "all 0.15s ease"

  },


  multiOptionActive: {

    background: "#f8fbff",

    border:
      "1px solid #93c5fd",

    color: "#1d4ed8"

  },


  specification: {

    marginTop: 14,

    padding: "14px",

    borderRadius: 12,

    background: "#f8fafc",

    border:
      "1px solid #e2e8f0"

  },


  specificationLabel: {

    display: "block",

    fontSize: 12,

    fontWeight: 700,

    color: "#64748b",

    marginBottom: 6

  },


  noQuestions: {

    padding: 25,

    borderRadius: 14,

    background: "#f8fafc",

    color: "#64748b",

    textAlign: "center",

    fontSize: 14

  }

};
